import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Scale, Loader2, ThumbsUp, ThumbsDown, Minus, History } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

const VERDICT = {
  strong_support: ['Strong support', 'text-emerald-700 dark:text-emerald-400 border-emerald-500'],
  moderate_support: ['Moderate support', 'text-emerald-700 dark:text-emerald-400 border-emerald-400'],
  contested: ['Contested', 'text-amber-700 dark:text-amber-400 border-amber-500'],
  moderate_against: ['Moderate evidence against', 'text-rose-700 dark:text-rose-400 border-rose-400'],
  strong_against: ['Strong evidence against', 'text-rose-700 dark:text-rose-400 border-rose-500'],
  insufficient: ['Insufficient evidence', 'text-slate-600 dark:text-slate-300 border-slate-400'],
};
const STANCE = {
  supports: ['Supports', 'border-emerald-500 text-emerald-700 dark:text-emerald-400', ThumbsUp],
  refutes: ['Refutes', 'border-rose-500 text-rose-700 dark:text-rose-400', ThumbsDown],
  mixed: ['Mixed', 'border-amber-500 text-amber-700 dark:text-amber-400', Minus],
  not_relevant: ['Not relevant', 'border-slate-400 text-slate-500', Minus],
};

export default function ConsensusMeterPanel({ userId, projectId }) {
  const [statement, setStatement] = useState('');
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const poll = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await thesisApi.listConsensus(userId, projectId);
      setHistory(res.reports || []);
      if (!result && res.reports?.length) setResult(res.reports[0].result);
    } catch (e) { /* no reports yet */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, projectId]);

  useEffect(() => {
    loadHistory();
    return () => { if (poll.current) clearInterval(poll.current); };
  }, [loadHistory]);

  const run = async () => {
    if (statement.trim().length < 12) return;
    try {
      const { job_id } = await thesisApi.startConsensus(userId, projectId, {
        statement: statement.trim(), max_papers: 14 });
      setJob({ status: 'queued', progress: 'Selecting the most relevant papers…' });
      let ticks = 0;
      poll.current = setInterval(async () => {
        ticks += 1;
        if (ticks > 100) {          // hard cap ~5 min
          clearInterval(poll.current);
          setJob(null);
          toast.error('Consensus run timed out — try again with fewer papers');
          return;
        }
        try {
          const j = await thesisApi.insightJob(userId, job_id);
          setJob(j);
          if (j.status === 'ready') {
            clearInterval(poll.current);
            setJob(null);
            setResult(j.result);
            loadHistory();
            toast.success('Consensus map ready');
          } else if (j.status === 'failed') {
            clearInterval(poll.current);
            setJob(null);
            toast.error(j.error || 'Consensus run failed');
          }
        } catch (e) {
          clearInterval(poll.current);
          setJob(null);
          toast.error(e.response?.data?.detail || e.message);
        }
      }, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const agg = result?.aggregate;
  const [vLabel, vClass] = VERDICT[result?.verdict] || VERDICT.insufficient;

  return (
    <Card data-testid="consensus-panel">
      <CardHeader>
        <CardTitle className="font-serif flex flex-wrap items-center gap-2"
          style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
          <Scale className="w-5 h-5 text-[#1d4ed8]" /> Consensus meter
          {result && <Badge variant="outline" className={`text-[10px] ${vClass}`}
            data-testid="consensus-verdict">{vLabel}</Badge>}
        </CardTitle>
        <CardDescription>
          State a claim as a full sentence. InkPhD reads your library paper by paper and shows how
          many studies support it, refute it or are mixed — weighted by study design, with the
          verbatim sentence it judged each paper on.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea value={statement} onChange={(e) => setStatement(e.target.value)}
          rows={2} data-testid="consensus-statement-input"
          placeholder="e.g. Early enteral nutrition reduces mortality in severe acute pancreatitis." />
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={run} disabled={!!job || statement.trim().length < 12}
            data-testid="consensus-run-btn">
            {job ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Scale className="w-4 h-4 mr-2" />}
            {job ? 'Judging papers…' : 'Build consensus map'}
          </Button>
          {job && <span className="text-[11px] text-muted-foreground"
            data-testid="consensus-progress">{job.progress}</span>}
          {!!history.length && (
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
              <History className="w-3 h-3" /> {history.length} saved
            </span>
          )}
        </div>

        {result && (
          <div className="space-y-4" data-testid="consensus-result">
            <div className="p-3 rounded-lg border border-border space-y-2">
              <p className="text-[12px] font-medium">{result.statement}</p>
              <div className="h-4 w-full rounded-full overflow-hidden flex bg-muted"
                data-testid="consensus-bar">
                <div className="bg-emerald-500 h-full transition-all"
                  style={{ width: `${agg?.support_percent || 0}%` }}
                  title={`Supports ${agg?.support_percent || 0}%`} />
                <div className="bg-amber-400 h-full transition-all"
                  style={{ width: `${agg?.mixed_percent || 0}%` }}
                  title={`Mixed ${agg?.mixed_percent || 0}%`} />
                <div className="bg-rose-500 h-full transition-all"
                  style={{ width: `${agg?.refute_percent || 0}%` }}
                  title={`Refutes ${agg?.refute_percent || 0}%`} />
              </div>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline" className="border-emerald-500 text-emerald-700 dark:text-emerald-400">
                  {agg?.counts?.supports || 0} support
                </Badge>
                <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                  {agg?.counts?.mixed || 0} mixed
                </Badge>
                <Badge variant="outline" className="border-rose-500 text-rose-700 dark:text-rose-400">
                  {agg?.counts?.refutes || 0} refute
                </Badge>
                <Badge variant="outline">{agg?.counts?.not_relevant || 0} not relevant</Badge>
                <Badge variant="outline" title="Weighted by study design (meta-analysis > RCT > cohort …)">
                  design-weighted support {agg?.weighted_support_percent || 0}%
                </Badge>
              </div>
              <p className="text-[12px]" data-testid="consensus-summary">{result.summary}</p>
              {!!result.caveats?.length && (
                <ul className="text-[11px] text-muted-foreground list-disc list-inside">
                  {result.caveats.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}
            </div>

            <div className="space-y-2">
              {result.papers?.map((p) => {
                const [label, cls, Icon] = STANCE[p.stance] || STANCE.not_relevant;
                return (
                  <div key={`${p.index}-${p.doc_id}`} className="p-2 rounded border border-border space-y-1"
                    data-testid={`consensus-paper-${p.index}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className={`text-[9px] ${cls}`}>
                        <Icon className="w-2.5 h-2.5 mr-1" />{label}
                      </Badge>
                      <Badge variant="outline" className="text-[9px]">{p.design}</Badge>
                      {p.year && <Badge variant="outline" className="text-[9px]">{p.year}</Badge>}
                      <span className="text-[12px] font-medium truncate max-w-full">{p.title}</span>
                    </div>
                    {p.note && <p className="text-[11px] text-muted-foreground">{p.note}</p>}
                    {p.evidence_quote && (
                      <p className="text-[11px] italic border-l-2 border-[#1d4ed8]/40 pl-2">
                        “{p.evidence_quote}”
                      </p>
                    )}
                    {p.doi && (
                      <a className="text-[10px] text-[#1d4ed8] hover:underline"
                        href={`https://doi.org/${p.doi}`} target="_blank" rel="noreferrer">
                        doi:{p.doi}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
